import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Alert, Avatar, Box, Button, CircularProgress, LinearProgress, ListItemIcon, Menu, MenuItem, Stack, Typography } from '@mui/material';
import React from 'react';
import { useHistory } from 'react-router-dom';
import brandLockup from '../asset/logo/lockup-horizontal-color.svg';
import { IUser, UserApi } from '../api/UserApi';
import { BLUE_TEXT, BORDER, ERROR_TEXT, INTER, NAVY, PAGE_BACKGROUND, POPPINS, SLATE, SURFACE_MUTED } from '../style/verbaScholaStyles';

interface VerbaScholaTeacherShellProps {
    title: React.ReactNode | ((profile: IUser | null) => React.ReactNode);
    titleAdornment?: React.ReactNode;
    titleAdornmentFullWidth?: boolean;
    subtitle?: string | null;
    contextLabel?: React.ReactNode;
    backTo?: string;
    backLabel?: string;
    headerVariant?: 'page' | 'workspace' | 'builder';
    /** Null until the first load resolves; the shell renders a spinner meanwhile. */
    loading: boolean;
    /** Show a non-blocking indicator while an already-rendered page refreshes. */
    refreshing?: boolean;
    error: string | null;
    onRetry?: () => void;
    actions?: React.ReactNode;
    navigation?: React.ReactNode;
    wideWorkspace?: boolean;
    children: React.ReactNode;
}

function displayName(user: IUser | null): string {
    const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    return name || user?.email || 'Teacher';
}

function profileInitials(user: IUser | null): string {
    const first = user?.first_name?.trim().charAt(0) ?? '';
    const last = user?.last_name?.trim().charAt(0) ?? '';
    const initials = `${first}${last}`.toUpperCase();
    if (initials) return initials;
    return (user?.email?.trim().charAt(0) || 'T').toUpperCase();
}

/**
 * Page frame shared by every production VerbaSchola teacher page.
 *
 * The mockups have no loading, error, or empty states because their data is a local constant.
 * Real pages need all three, so the shell owns them and each page renders only its content.
 */
export default function VerbaScholaTeacherShell({
    title,
    titleAdornment,
    titleAdornmentFullWidth = false,
    subtitle,
    contextLabel,
    backTo,
    backLabel,
    headerVariant = 'page',
    loading,
    refreshing = false,
    error,
    onRetry,
    actions,
    navigation,
    wideWorkspace = false,
    children,
}: VerbaScholaTeacherShellProps) {
    const history = useHistory();
    const [profileAnchor, setProfileAnchor] = React.useState<HTMLElement | null>(null);
    const [profile, setProfile] = React.useState<IUser | null>(null);

    React.useEffect(() => {
        let cancelled = false;
        UserApi.get()
            .then(response => {
                if (!cancelled) setProfile(response.data);
            })
            .catch(() => {
                if (!cancelled) setProfile(null);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const closeProfileMenu = () => setProfileAnchor(null);
    const titleText = typeof title === 'function' ? title(profile) : title;
    const titleContent = (
        <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
                <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: { xs: 25, sm: 30 }, letterSpacing: 0, color: NAVY }}>
                    {titleText}
                </Typography>
                {!titleAdornmentFullWidth && titleAdornment}
            </Stack>
            {subtitle && (
                <Typography sx={{ fontFamily: INTER, fontSize: 13.5, color: SLATE, mt: 0.5 }}>{subtitle}</Typography>
            )}
        </Box>
    );
    const titleBlock = (
        <>
            {contextLabel}
            {titleAdornmentFullWidth ? (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) auto' },
                    gridTemplateAreas: { xs: '"title" "status" "actions"', md: '"title actions" "status status"' },
                    alignItems: 'center',
                    gap: 2,
                }}>
                    <Box sx={{ gridArea: 'title', minWidth: 0 }}>{titleContent}</Box>
                    <Box sx={{ gridArea: 'status' }}>{titleAdornment}</Box>
                    {actions && <Box sx={{ gridArea: 'actions' }}>{actions}</Box>}
                </Box>
            ) : (
                <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2}>
                    {titleContent}
                    {actions}
                </Stack>
            )}
            {navigation}
        </>
    );

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: PAGE_BACKGROUND }}>
            <Box
                component="header"
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 5,
                    minHeight: 72,
                    px: { xs: 2, sm: 3, lg: 4 },
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'rgba(255,255,255,0.96)',
                    borderBottom: `1px solid ${BORDER}`,
                    backdropFilter: 'blur(10px)',
                }}
            >
                <Button onClick={() => history.push('/teacher')} sx={{ p: 0, minWidth: 0, textTransform: 'none' }}>
                    <Stack direction="row" alignItems="center" spacing={1.4}>
                        <Box component="img" src={brandLockup} alt="VerbaSchola" sx={{ height: { xs: 22, sm: 25 }, width: 'auto', display: 'block' }} />
                        <Box sx={{ display: headerVariant === 'builder' ? 'none' : { xs: 'none', sm: 'block' }, width: '1px', height: 20, backgroundColor: BORDER }} />
                        <Typography sx={{ display: headerVariant === 'builder' ? 'none' : { xs: 'none', sm: 'block' }, fontFamily: INTER, fontWeight: 600, fontSize: 11, color: SLATE, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Teacher</Typography>
                    </Stack>
                </Button>

                {headerVariant === 'builder' && <Box sx={{ ml: { xs: 1.5, md: 4 }, minWidth: 0 }}>{navigation}</Box>}
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 1 } }}>
                    {headerVariant === 'builder' && backTo && <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => history.push(backTo)}
                        sx={{ display: { xs: 'none', md: 'inline-flex' }, minHeight: 44, color: SLATE, fontFamily: INTER, fontWeight: 600, fontSize: 12.5, textTransform: 'none', whiteSpace: 'nowrap' }}>
                        {backLabel ?? 'Back'}
                    </Button>}
                    {headerVariant === 'builder' && actions}
                    <Button
                        onClick={event => setProfileAnchor(event.currentTarget)}
                        aria-haspopup="menu"
                        aria-expanded={Boolean(profileAnchor)}
                        sx={{ minWidth: 0, p: 0.75, borderRadius: '12px', textTransform: 'none', '&:hover': { backgroundColor: SURFACE_MUTED } }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 38, height: 38, backgroundColor: NAVY, fontFamily: POPPINS, fontSize: 13 }}>{profileInitials(profile)}</Avatar>
                            <Typography sx={{ display: headerVariant === 'builder' ? 'none' : { xs: 'none', lg: 'block' }, fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: NAVY, lineHeight: 1.15, textAlign: 'left' }}>
                                {displayName(profile)}
                            </Typography>
                            <KeyboardArrowDownRoundedIcon sx={{ display: headerVariant === 'builder' ? 'none' : { xs: 'none', sm: 'block' }, ml: 0.25, color: SLATE, fontSize: 19 }} />
                        </Stack>
                    </Button>
                    <Menu
                        anchorEl={profileAnchor}
                        open={Boolean(profileAnchor)}
                        onClose={closeProfileMenu}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{ sx: { minWidth: 190, mt: 0.75, border: `1px solid ${BORDER}`, borderRadius: '13px', boxShadow: '0 10px 30px rgba(22, 58, 99, 0.12)' } }}
                    >
                        <MenuItem
                            onClick={() => {
                                closeProfileMenu();
                                history.push('/teacher/account');
                            }}
                            sx={{ minHeight: 44, fontFamily: INTER, fontSize: 12.5, color: NAVY }}
                        >
                            <ListItemIcon sx={{ minWidth: '32px !important', color: SLATE }}><SettingsOutlinedIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                            Account and password
                        </MenuItem>
                        <MenuItem onClick={() => { closeProfileMenu(); history.push('/teacher/help'); }} sx={{ minHeight: 44, fontFamily: INTER, fontSize: 12.5, color: NAVY }}>
                            <ListItemIcon sx={{ minWidth: '32px !important', color: SLATE }}><HelpOutlineRoundedIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                            Help & support
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                closeProfileMenu();
                                localStorage.removeItem('jwt');
                                history.push('/login');
                            }}
                            sx={{ minHeight: 44, fontFamily: INTER, fontSize: 12.5, color: ERROR_TEXT }}
                        >
                            <ListItemIcon sx={{ minWidth: '32px !important', color: ERROR_TEXT }}><LogoutRoundedIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                            Sign out
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>

            <Box component="main">
            {headerVariant === 'workspace' && (
                <Box component="section" aria-label="Teacher workspace heading" sx={{ backgroundColor: '#fff', borderBottom: `1px solid ${BORDER}` }}>
                    <Box sx={{ width: '100%', maxWidth: wideWorkspace ? 'none' : 1220, mx: 'auto', px: { xs: 2, sm: 3, lg: 4 }, pt: { xs: 2.5, md: 3.5 } }}>
                        {backTo && (
                            <Button
                                startIcon={<ArrowBackRoundedIcon />}
                                onClick={() => history.push(backTo)}
                                sx={{ minHeight: 44, fontFamily: INTER, fontWeight: 600, fontSize: 12.5, color: SLATE, textTransform: 'none', mb: 1, ml: -1 }}
                            >
                                {backLabel ?? 'Back'}
                            </Button>
                        )}
                        {titleBlock}
                    </Box>
                </Box>
            )}

            <Box sx={{ width: '100%', maxWidth: wideWorkspace ? 'none' : (headerVariant === 'workspace' ? 1220 : 1180), mx: 'auto', ...(headerVariant === 'builder' ? { px: { xs: 2, sm: 3, lg: 4 }, py: 2 } : { p: { xs: 2, sm: 3, lg: 4 } }) }}>
                {headerVariant === 'builder' && backTo && <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => history.push(backTo)}
                    sx={{ display: { xs: 'inline-flex', md: 'none' }, minHeight: 44, color: SLATE, fontFamily: INTER, fontWeight: 600, fontSize: 12.5, textTransform: 'none', mb: 1, ml: -1 }}>
                    {backLabel ?? 'Back'}
                </Button>}
                {headerVariant === 'page' && backTo && (
                    <Button
                        startIcon={<ArrowBackRoundedIcon />}
                        onClick={() => history.push(backTo)}
                        sx={{ minHeight: 44, fontFamily: INTER, fontWeight: 600, fontSize: 12.5, color: SLATE, textTransform: 'none', mb: 1, ml: -1 }}
                    >
                        {backLabel ?? 'Back'}
                    </Button>
                )}

                {headerVariant === 'page' && titleBlock}

                <Box sx={{ mt: headerVariant === 'page' ? 3.5 : 0 }}>
                    {refreshing && <LinearProgress aria-label="Refreshing page" sx={{ mb: 2 }} />}
                    {error && (
                        <Alert
                            severity="error"
                            action={onRetry ? <Button color="inherit" size="small" onClick={onRetry}>Retry</Button> : undefined}
                            sx={{ fontFamily: INTER, mb: 2 }}
                        >
                            {error}
                        </Alert>
                    )}

                    {loading && !error ? (
                        <Stack alignItems="center" sx={{ py: 8 }}>
                            <CircularProgress size={28} aria-label="Loading teacher page" />
                        </Stack>
                    ) : (
                        children
                    )}
                </Box>
            </Box>
            </Box>
        </Box>
    );
}

/** Bordered white card used for every list and table on these pages. */
export function VerbaScholaCard({ children }: { children: React.ReactNode }) {
    return (
        <Box sx={{ backgroundColor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '20px', overflow: 'hidden' }}>
            {children}
        </Box>
    );
}

export function VerbaScholaCardHeader({ title, meta, actions }: { title: string; meta?: string | null; actions?: React.ReactNode }) {
    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            useFlexGap
            sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' }, px: { xs: 2, sm: 2.75 }, py: 2.5, borderBottom: `1px solid ${BORDER}` }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>{title}</Typography>
                {meta && <Typography sx={{ fontFamily: INTER, fontSize: 12.5, color: SLATE, mt: 0.25 }}>{meta}</Typography>}
            </Box>
            {/* Actions sit beside the title when they fit. On phones they wrap below it, still at the right. */}
            {actions && <Box sx={{ ml: 'auto' }}>{actions}</Box>}
        </Stack>
    );
}

/**
 * Initials for a roster avatar, matching the mockup's two-letter badges.
 *
 * The label can be a full name or, when the account has no name, an email address. An email
 * has no second word worth abbreviating, so it yields a single letter rather than the two
 * meaningless characters that splitting on "@" or "." would produce.
 */
export function verbaScholaInitials(label: string | null, fallbackId: number): string {
    if (!label) return String(fallbackId);
    if (label.includes('@')) return label.charAt(0).toUpperCase();
    const wordList = label.split(/\s+/).filter(Boolean);
    if (wordList.length === 0) return String(fallbackId);
    const first = wordList[0].charAt(0);
    const last = wordList.length > 1 ? wordList[wordList.length - 1].charAt(0) : '';
    return `${first}${last}`.toUpperCase();
}

/** Roster avatar. The two-tone alternation by row index is the mockup's, kept deliberately. */
export function VerbaScholaStudentAvatar({ label, fallbackId, index }: { label: string | null; fallbackId: number; index: number }) {
    return (
        <Avatar sx={{ width: 36, height: 36, backgroundColor: index % 2 ? SLATE : BLUE_TEXT, fontFamily: POPPINS, fontSize: 11.5 }}>
            {verbaScholaInitials(label, fallbackId)}
        </Avatar>
    );
}

/** Shown when a list is legitimately empty, which the mockups never depict. */
export function VerbaScholaEmptyState({ message }: { message: string }) {
    return (
        <Typography sx={{ fontFamily: INTER, fontSize: 13.5, color: SLATE, px: { xs: 2, sm: 2.75 }, py: 5, textAlign: 'center' }}>
            {message}
        </Typography>
    );
}
